export default {
  pageTitle: 'Create bulk order',
  // Says nothing about how many orders result: one per vendor (FR-3), which the split note on the
  // footer states once a selection exists and the count is actually known.
  pageSubtitle: 'Order across several sites in a project.',

  steps: {
    items: { label: 'Items', hint: 'Choose items' },
    sites: { label: 'Sites', hint: 'Pick sites' },
    allocate: { label: 'Allocate', hint: 'Split quantities' },
    /**
     * Named rather than numbered, which is the ground DL-56 turns on: a step named in the progress
     * indicator advertises that an action is owed before the officer reaches it.
     *
     * ⚠️ The label names payment, which release 1 does not yet collect — PE-882 fills the step.
     * The hint states what the step actually asks today and changes with it.
     */
    payment: { label: 'Payment & delivery', hint: 'Terms & logistics' },
    review: { label: 'Review', hint: 'Confirm and place' },
  },

  actions: {
    back: 'Back',
    next: 'Next',
    cancel: 'Cancel',
    /** Step 4's primary action. Its wiring is batch A.4; the shell only makes it primary. */
    place: 'Request all orders',
  },

  /**
   * Why Next is unavailable, rendered **beside** the button rather than leaving it bare.
   *
   * Context: a disabled control with no reason reads as a broken screen. Each of these states the
   * condition in the user's terms, and step 2's is a route rather than a refusal.
   */
  gates: {
    noProject: 'Choose a project to start.',
    noItems: 'Add at least one item to continue.',
    /**
     * The two-site hard gate (FR-4). Split so the link is a real route, not words: a user with one
     * site is being **routed, not corrected**, so the tone stays neutral and the link is styled
     * neutrally rather than as an error.
     */
    twoSitesPrefix: 'A bulk order needs at least 2 sites. For a single site,',
    twoSitesLink: 'use the standard order flow',
    markAllDone: 'Mark all {total} items as done to continue ({done} of {total} done).',
    allDone: 'All {total} items marked done',
    /**
     * Step 4's gate. A gate rather than the validate-on-press treatment this field had on Review:
     * there the primary action was Place, here it is Next, and every other step in this wizard
     * states its reason beside a blocked Next.
     */
    noDeliveryDate: 'Choose a delivery date to continue.',
  },

  /** Exit confirm. Covers in-app navigation; a refresh or closed tab gets the browser's own prompt. */
  leaveDialog: {
    title: 'Discard this bulk order?',
    body: 'Your in-progress selection will be lost. Bulk orders cannot be saved as a draft yet, so leaving now starts over.',
    confirm: 'Discard and exit',
    cancel: 'Keep editing',
  },

  /** The multi-vendor split note (FR-3). Announced when it appears, and cleared when it stops applying. */
  splitNote: {
    single: 'All items share one vendor, so this places 1 bulk order.',
    singleShort: 'Single vendor: this places 1 bulk order.',
    multiple:
      'Your selection spans {count} vendors, so it splits into {count} bulk orders (one per vendor) at Review.',
  },

  /**
   * Step 3 — Allocate (S-05).
   *
   * ⚠️ **No em dashes.** In-app copy is gated on it, so the design pack's em dashes in the
   * Allocate-remaining dialog body and the disabled tooltip are colons here (DL-83's wording is
   * otherwise preserved). The one exception is a BOQ line's own name, which comes from the
   * server verbatim.
   */
  allocate: {
    /**
     * PE-691 AC28. A count of rows rather than of errors, because a row is what the officer opens
     * and fixes — and the server's own message rides on the row itself, where it points at the
     * thing it is about.
     */
    refused: {
      returned:
        'One row cannot be ordered as it stands. It is marked below with the reason. | {count} rows cannot be ordered as they stand. They are marked below with the reason.',
    },
    progress: '{done} of {total} items allocated',
    progressLabel: 'Items marked done',

    filter: {
      groupLabel: 'Filter items',
      all: 'All',
      todo: 'To do',
      variations: 'Variations',
      emptyTodo: 'All items allocated, nothing left to do',
      emptyVariations: 'No items with variations',
    },

    tile: {
      groupLabel: 'Choose an item to allocate',
      /** Both tile numbers carry one, and both are reachable by tap as well as hover. */
      siteCountTooltip: 'Allocated to {done} of {total} selected sites',
      variationCountTooltip:
        '{count} site will raise a variation | {count} sites will raise a variation',
      doneTooltip: 'Marked done',
      /** Whole Rand on the tile; the tooltip is where the cents live. */
      totalTooltip: 'Item total {total}',
      siteCount: '{count} site | {count} sites',
      /** The rate keeps its cents — a rate is read, not scanned, and the tile's total is the rounded one. */
      rate: '{rate}/{unit}',
    },

    header: {
      crossSiteTotal: 'Cross-site total',
      sitesSummary: '{quantity} {unit} · {count} site | {quantity} {unit} · {count} sites',
    },

    allocatingTo: {
      label: 'Allocating to',
      coverage: '{onBill} of {total} in bill',
      coverageAllTooltip:
        'Every site this item is allocated to already carries it on its bill, so none of them raises a variation for being off bill.',
      coverageSomeTooltip:
        '{count} of these sites do not carry this item on their bill yet. Ordering against them adds a line, which raises a variation on each.',
      addSite: 'Add a site',
      addSiteShort: 'Add site',
      addSiteMenuLabel: 'Add a site to this item',
      onBillGroup: 'In this item’s bill',
      /** States the consequence at the point of choosing, not afterwards (DL-17). */
      offBillGroup: 'Off bill, raises variation',
      offBillMarker: 'Off bill',
    },

    completion: {
      markDone: 'Mark item done',
      /** The same action on a phone, where it shares its line with nothing that names the item. */
      markDoneShort: 'Mark done',
      isDone: 'This item is done',
      reopen: 'Reopen to edit',
      doneBanner: 'Marked done, you can still edit this item.',
      /**
       * The disabled reasons, one per blocker: each names its own fix, because each has a different
       * one. A generic "not ready" leaves the user hunting a fifty-row grid for what is missing.
       */
      markDoneBlocked: 'Add a site and a quantity before marking this item done.',
      markDoneBlockedEmptySite:
        '{count} site has no BOQ line on it yet. | {count} sites have no BOQ line on them yet.',
      markDoneBlockedUnbound:
        '{count} line is not pointing at a BOQ line yet. Choose one, or remove the line. | {count} lines are not pointing at a BOQ line yet. Choose one, or remove them.',
      markDoneBlockedQuantity:
        '{count} line has no quantity. Enter one, or remove the line. | {count} lines have no quantity. Enter one, or remove the line.',
      markDoneBlockedSpecification:
        '{count} new line still needs a specification. | {count} new lines still need a specification.',
    },

    table: {
      searchPlaceholder: 'Search sites in this item',
      /** Neither the preview nor the sites cache could name it. A raw id helps nobody. */
      unknownSite: 'Unnamed site',
      /** The item has no sites at all. Says what to do, because the control for it is just above. */
      /**
       * 🔴 The step's own empty state, for an item selection that resolves to nothing. It rendered
       * the **Review** placeholder's copy (PR #2261 review), so a user on Allocate read a sentence
       * about a step they had not reached.
       */
      noItemTitle: 'No item to allocate',
      noItemBody: 'Go back to Items and choose at least one item to allocate across your sites.',
      noSitesTitle: 'No sites on this item yet',
      noSitesBody: 'Add a site to start allocating quantities.',
      /** Captions on the site rollup row. The column headers name the child rows' controls. */
      siteQuantityCaption: 'Site qty',
      siteTotalCaption: 'Site total',
      columns: {
        binding: 'Site / BOQ line',
        quantity: 'Quantity',
        variation: '',
        value: 'Remaining',
        total: 'Total',
        actions: '',
      },
      boqLineCount: '{count} BOQ line | {count} BOQ lines',
      addLine: 'Add line',
      /** A site row opened before it has a line: what to do next, not just that it is empty. */
      siteEmpty: 'Add a line to allocate to this site.',
      /** The site row's summed marker, plural beside its lines' own singular `variation.tag`. */
      siteVariationTag: 'Variations',
      change: 'Change',
      /** Amber, and now the genuine exception rather than the default on every row. */
      noMatch: 'New line, no match',
      removeSite: 'Remove site',
      removeLine: 'Remove line',
      expandSite: 'Show BOQ lines for {site}',
      collapseSite: 'Hide BOQ lines for {site}',
      /**
       * The BOQ line's own outstanding **budgeted** quantity — what it was budgeted for, less what
       * has already been ordered against it.
       *
       * The label stays short, because it sits under a column already headed "Remaining" and a
       * second word there is a word the reader has to re-parse on every row. The **tooltip** is
       * where it is said precisely: it used to claim this was what the site "needs", which is a
       * statement about the work and the one thing this figure does not know.
       */
      remainingQuantity: '{quantity} remaining',
      remainingQuantityTooltip:
        'This BOQ line has {quantity} budgeted and not yet ordered. Click to fill the row with it.',
      remainingBudget: '{amount} left',
      /**
       * The narrow breakpoint's quantity field has no column header over it, so it carries its own
       * name — and the name has to say which line, since a card list puts several of them on screen
       * at once with nothing but position to tell them apart.
       */
      quantityFieldLabel: 'Quantity for {line}',
      noneRemaining: '0 remaining',
      itemTotal: 'Item total · {count} site | Item total · {count} sites',
      paused: 'Paused',
      totalsAnnouncement: 'Item total {item}. Order total {order}.',
    },

    variation: {
      tag: 'Variation',
      gatingHeading: 'Variation, needs approval',
      nonGatingHeading: 'Variation, not blocking',
      /**
       * Icon-only on the parent row, and **both sentences render where both apply** — a site can
       * carry one of each, and saying only the louder half hid the other entirely.
       *
       * The breakdown beneath lists the gating lines, because those are the ones that increase the
       * budget; the quiet sentence is what accounts for the rest.
       */
      siteGating:
        '{count} line here raises a variation that needs approval. | {count} lines here raise variations that need approval.',
      // Context: a variation that does not gate is a rate below the site's own line rate. Its scope
      // is chosen when the bulk order is approved, not here (DL-149, DL-150).
      siteNonGating:
        '{count} other raises a variation that does not need approval. Its rate is below the BOQ line rate, and it is approved with the bulk order at a scope chosen then. | {count} others raise variations that do not need approval. Their rates are below the BOQ line rates, and they are approved with the bulk order at a scope chosen then.',
      /**
       * The delta rows render label, from, arrow and to as separate elements (the design's shape),
       * so the label and the two figures are keys rather than one interpolated sentence.
       */
      quantityLabel: 'Qty',
      rateLabel: 'Rate',
      lineTotal: 'This order line total',
      /** The difference the approver is being asked to accept: the line's total after, less now. */
      budgetIncrease: 'BOQ budget increases by',
      /**
       * ⚠️ **Stated, never celebrated.** Requirements §8 #5 is explicit that nothing captures,
       * credits or reports a saving, so this says what the budget does rather than calling it one.
       */
      budgetDecrease: 'BOQ budget reduces by',
      /** The remainder row on a site's breakdown. It carries its own sum, so the column adds up. */
      siteMoreLines: '{count} more line | {count} more lines',
      /**
       * The outcome, not a promise: whether the line rate changes is the approver's choice
       * (DL-150), so Allocate states what this order pays and what the line keeps until then.
       */
      scopeStatement:
        'This order uses {orderRate}. This site’s BOQ line rate stays {lineRate} unless the variation approver changes it.',
      causes: {
        NewOrOffBillLine:
          'This line is not on the site’s bill. Approving adds it, which is what increases the budget.',
        OverBudgetQuantity: 'More is ordered than the line still has outstanding.',
        RateChange: 'The order rate differs from this site’s own BOQ line rate.',
      },
    },

    rate: {
      label: 'Rate',
      revert: 'Revert to vendor price',
      revertTooltip: 'Set the rate back to {rate}, the vendor’s listed price.',
      /** Compares to the VENDOR'S listed price, which is one number for the item. */
      statement: '{amount} {direction} the vendor’s listed price. May raise a variation per site.',
      above: 'above',
      below: 'below',
      /**
       * The full explanation, in **three short statements rather than one paragraph** — what the
       * two rates are, why that raises anything at all, and what it costs. It was one block of six
       * lines, which is a lot to parse from a hover. The figures interpolate through `<i18n-t>` so
       * the component can bold them without the sentence being cut into fragments here.
       *
       * Never implies a reduction is consequence-free, and never that a saving is captured,
       * credited or reported (requirements §8 #5).
       */
      explainerRates: 'The vendor lists this item at {vendorRate}. This order pays {orderRate}.',
      explainerWhy:
        'Each site’s BOQ line carries {ownRate}, so any site whose line rate differs raises a variation.',
      explainerOwnRate: 'its own rate',
      explainerIncrease:
        'An increase must be approved on each affected site before this bulk order can be approved. The approver chooses whether the new rate is permanent or for this order only.',
      explainerDecrease:
        'A reduction does not hold up approval. It is recorded and pending on the site’s line, and is approved with the bulk order, where the approver chooses whether the new rate is permanent or for this order only.',
    },

    allocateRemaining: {
      action: 'Allocate remaining',
      /** Colons, not em dashes, and the criterion is stated in words rather than implied. */
      disabledTooltip:
        'Available once every site has at least one BOQ line bound: the fill reads each line’s remaining quantity. Bind the outstanding sites first.',
      title: 'Allocate remaining to {count} site? | Allocate remaining to {count} sites?',
      body: 'Fills {rows} empty row across {sites} site with each site’s own remaining quantity: {quantity} {unit} in total. | Fills {rows} empty rows across {sites} sites with each site’s own remaining quantity: {quantity} {unit} in total.',
      kept: '{count} site already has a quantity ({names}) and will not be changed | {count} sites already have a quantity ({names}) and will not be changed',
      skipped:
        '{names} has nothing remaining and will stay blank | {names} have nothing remaining and will stay blank',
      confirm: 'Allocate remaining',
      cancel: 'Cancel',
      emptyTitle: 'Nothing to allocate',
      emptyBody:
        'Every site either already has a quantity or has nothing remaining to order. Nothing would change.',
      emptyClose: 'Close',
    },

    picker: {
      title: 'Choose a BOQ line at {site}',
      searchPlaceholder: 'Search by line item or specification',
      /** Announced once while the skeleton is up; the placeholders themselves stay hidden. */
      loading: 'Loading this site’s BOQ lines',
      /** The picker's own empties, on the feature's shared `EmptyState` like every other one. */
      emptyTitle: 'No matching BOQ lines',
      empty: 'No lines at this site match this item’s specification. Add it as a new line below.',
      noMatchesTitle: 'Nothing matches “{term}”',
      noMatchesDescription: 'Try a different line item or specification, or clear the search.',
      errorTitle: 'Couldn’t load this site’s BOQ lines',
      close: 'Close',
      /** Names what the list is: the lines at THIS site linked to the ordered item. */
      listCaption: 'Matching BOQ lines at this site',
      /** A search narrows the linked lines only (DL-189 P-01), so it still names them as matches. */
      listCaptionSearch: 'Matching BOQ lines at this site containing “{term}”',
      /** The row the picker was opened from is already on this line: named, not re-offered. */
      current: 'Selected',
      currentTooltip: 'This row is already on this line.',
      /** Says THAT a row cannot be picked; the row's own tooltip carries the server's reason. */
      unavailable: 'Unavailable',
      sectionPath: '{section} · {subSection}',
      moneyLeft: '{amount} left',
      selected: 'Selected',
      /** Rendered VERBATIM from the server; this client keeps no reason list of its own. */
      addNew: 'Add as a new line item',
      addNewConsequence: 'Not on this site’s bill. Raises a variation.',
      alreadyBound: 'Already used by another line on this site',
    },

    newLine: {
      heading: 'New BOQ line',
      variationNote: 'This adds a line to the site’s bill, so it is a variation pending approval.',
      name: 'Line item name',
      specification: 'Specification',
      section: 'BOQ section',
      subSection: 'Subsection',
      /** Optional: a line can sit directly under its section. */
      subSectionOptional: 'Search or add one (optional)',
      /** A disabled field with no reason on it reads as broken, so the placeholder carries one. */
      subSectionAwaitingSection: 'Choose a section first',
      /** The create-on-type row inside each placement combobox. */
      addNamed: 'Add “{name}”',
      sectionPlaceholder: 'Search or add a section',
      quantity: 'Quantity',
      rate: 'Rate',
      /**
       * 🔴 **Only the comparison, and only when there is one** (CPTO, 2026-09-16).
       *
       * Two sentences used to sit here saying that this is the order's rate and that the line joins
       * the bill at it once approved — the first restating the field's own label, the second
       * restating `variationNote` at the top of the same form. Both stood in front of the one fact
       * an officer is checking: what this line will be priced at. The figure above says that; this
       * says the other figure, and where the two match it does not render at all.
       */
      rateBasisOverridden: 'Vendor’s listed price is {amount}.',
      lineTotal: 'Line total',
      save: 'Add line',
      cancel: 'Cancel',
    },

    errors: {
      /** A banner at the head of the step, not a toast, and it never replaces the step. */
      partialLoad:
        'Couldn’t load some sites’ BOQ lines and remaining budgets, those rows are paused. Retry to allocate them.',
      retry: 'Retry',
      /**
       * ⚠️ **Written, agreed, and not yet wired** (PR #2261 review). DL-118's prune — re-validating
       * a rehydrated grid against the preview and dropping what the server no longer accepts — is
       * not built: `loadBasis` merges cells and drops no binding. DL-132's return out of Review is
       * A.4's. Both strings are kept rather than deleted so the agreed wording does not have to be
       * re-litigated when the behaviour lands; neither is referenced today.
       */
      pruned:
        '{count} row was removed because the server no longer accepts it. | {count} rows were removed because the server no longer accepts it.',
      /** DL-132: figures moved under the user mid-flow. The grid itself is untouched. Unwired. */
      staleFigures:
        '{count} row changed since you allocated it, so you are back on Allocate with the new figures. Your quantities are unchanged. | {count} rows changed since you allocated them, so you are back on Allocate with the new figures. Your quantities are unchanged.',
    },
  },

  sites: {
    intro: 'Choose which of the project’s sites this bulk order spans.',
    searchPlaceholder: 'Search sites',
    clearSelection: 'Clear selection',
    /** The narrow ladder's stand-in for the running count it deliberately does not render. */
    clearWithCount: 'Clear ({count})',

    /**
     * Shown only while the selection is still exactly what coverage suggested, and it keeps its
     * box when it goes — see `PreSelectionMessage.vue` for why that matters.
     */
    preSelectionMessage:
      'We’ve pre-selected the sites that already carry your items. Add or remove any as needed.',

    /**
     * The running count, in three parts because only the middle one is conditional. `N` counts
     * every selected site including the ones the narrowing has hidden; the bracketed clause
     * appears only when it is actually hiding some.
     */
    selectedCount: '{count} selected',
    hiddenCount: '({count} not shown by this filter)',
    variationCount: '{count} needs a variation | {count} need a variation',

    /**
     * ⚠️ Not 'select all live sites'. FR-7 was amended on 2026-09-10 — a planning site with an
     * approved BOQ is allocatable — and an ineligible site is not selectable at all, so the word
     * would narrow nothing even if it were still true.
     */
    selectAllLabel: 'Select all sites shown',

    columns: {
      site: 'Site',
      coverage: 'In bill',
    },

    row: {
      selectLabel: 'Select {site}',
      /**
       * An ineligible row's exclusion reason reaches assistive tech through the checkbox's own
       * name, so it is not only drawn. `{reason}` is the server's string, rendered verbatim.
       */
      ineligibleLabel: '{site}. Not available: {reason}',
    },

    coverage: {
      inBill: 'In bill',
      missing: '{count} missing',
      explainerIntro:
        'Of your {count} selected item, this site’s bill carries: | Of your {count} selected items, this site’s bill carries:',
      carriesGroup: 'Carries {count}',
      missingGroup: 'Missing {count}',
    },

    /** No chip label carries the word 'sites', so the narrow ladder has nothing to drop. */
    /** Asked only when an item holds quantities at the sites going (PE-989). */
    removeDialog: {
      titleOne: 'Remove {site}?',
      titleMany: 'Clear the selection?',
      bodyOne:
        'Removing {site} clears the quantities for {count} item there. | Removing {site} clears the quantities for {count} items there.',
      bodyMany:
        'Clearing these sites removes the quantities set for {count} item. | Clearing these sites removes the quantities set for {count} items.',
      confirm: 'Remove and clear',
      cancel: 'Keep what I have',
    },

    chips: {
      groupLabel: 'Filter sites',
      all: 'All',
      full: 'Full coverage',
      missing: 'Missing items',
      /** 'Due now', never 'in draw period'. Its explainer comes from PE-672's shared string. */
      dueNow: 'Due now',
      /** One per chip, so every chip explains itself the same way (PE-984 S3). */
      explainers: {
        all: 'Every site in this project.',
        full: 'Sites whose bill has every item you selected.',
        missing: 'Sites whose bill is missing at least one item you selected.',
      },
    },

    empty: {
      noSites: {
        title: 'This project has no sites yet',
        description:
          'Add a site to the project before creating a bulk order: a bulk order must span at least two sites.',
      },
      /** Its own state, not a failed search: nothing is wrong, the officer is ahead of every draw. */
      dueNow: {
        title: 'No sites are due now',
        description:
          'None of your selected items is currently due at any site on this project. Ordering ahead of a draw is still allowed.',
        action: 'Show all sites',
      },
      noSearchMatch: 'No sites match “{search}”.',
      noFilterMatch: 'No sites match this filter.',
    },

    /** Announced once while the structural skeleton is up; the placeholders themselves stay silent. */
    loading: 'Loading the project’s sites…',

    /** Says the selection survived, because the retry beside it would otherwise look destructive. */
    error: 'We could not load this project’s sites. Your selection has been kept.',
    retry: 'Try again',
  },

  items: {
    /** No project chosen: the picker stands alone over the design system's base empty table. */
    noProject: {
      title: 'Select a project',
      description: 'Choose a project to see the items on its sites’ bills.',
    },

    /**
     * The one bill state the user cannot resolve on this screen, so it is the only one that sends
     * them elsewhere. Says what is missing and where to fix it — never why, because the reason
     * (no sites yet, all archived) is the project's to show.
     */
    /** The wizard cannot start without the project list, so this one offers a retry. */
    projectsError: {
      title: 'Couldn’t load your projects',
      description: 'The project list didn’t load, so there’s nothing to choose from yet.',
      retry: 'Try again',
    },

    noSites: {
      title: 'No sites to order against',
      description: '{name} has no active sites, so there are no bills to order from.',
      descriptionUnnamed: 'This project has no active sites, so there are no bills to order from.',
      action: 'Open the project’s sites',
    },

    project: {
      label: 'Project',
      placeholder: 'Select a project',
      search: 'Search projects',
      /**
       * Shown beneath a project the viewer may see but not order against. Disabled rather than
       * hidden: a user can legitimately view a project and not initiate on it, and dropping it
       * silently reads as missing data.
       */
      notProcurementOfficer: 'You’re not a procurement officer on this project',
    },

    /** Raised **before** any reload, and only when there is something to lose. */
    /** Asked only when something later steps hold would be lost (PE-989). */
    removeDialog: {
      title: 'Remove {item}?',
      body: 'Removing {item} clears its quantities on {count} site. | Removing {item} clears its quantities on {count} sites.',
      bodyNoSites: 'Removing {item} clears what you set for it on Allocate.',
      payment: 'The payment details for {vendor} go too.',
      confirm: 'Remove and clear',
      cancel: 'Keep what I have',
    },

    switchDialog: {
      title: 'Switch project?',
      body: 'Switching project will clear the {count} item you’ve selected. Continue? | Switching project will clear the {count} items you’ve selected. Continue?',
      confirm: 'Switch project',
      cancel: 'Cancel',
    },

    onBill: {
      heading: 'In this project’s bill ({count})',
      searchPlaceholder: 'Search this project’s bill',
      searchPlaceholderShort: 'Search this bill',
      clearSearch: 'Clear search',
    },

    catalogue: {
      heading: 'Search the full vendor catalogue',
      subheading: 'Add items outside this project’s bill',
      browse: 'Browse',
      hide: 'Hide',
      /** Stated on the disclosure itself, so it is read before anything is added from it. */
      variationNote:
        'From vendor catalogues, not in this bill. Adding raises a variation when you allocate.',
      countLabel: '{count} match | {count} matches',
    },

    filters: {
      trigger: 'Filters',
      triggerWithCount: 'Filters ({count} active)',
      type: 'Type',
      vendor: 'Vendor',
      searchVendors: 'Search vendors',
      // Shown only once the list is long enough that scanning it stops being viable. States the
      // size so the reader knows the scroll has an end, and points at the search rather than
      // making them find it.
      vendorCountHint: '{count} vendors · type to narrow the list',
      clear: 'Clear filters',
      noMatches: 'No matches',
    },

    columns: {
      item: 'Item',
      coverage: 'Coverage',
      rate: 'Rate',
      action: 'Add',
    },

    row: {
      /**
       * Pluralised on the TOTAL — "On 1 of 2 sites" is right, "On 1 of 1 sites" is not. A
       * single-eligible-site project reaches this step: the two-site rule (FR-4) is gated on the
       * Sites step, not here.
       *
       * The total is therefore named `count`, and the numerator is `onCount`. vue-i18n selects the
       * form from `named.count` when that key is present and **ignores the explicit plural
       * argument**, so naming these the other way round silently pluralised on the numerator.
       */
      coverage: 'On {onCount} of {count} site | On {onCount} of {count} sites',
      remaining: '{amount} remaining',
      /** Off-bill rows have no BOQ line, so there is no remaining budget to state. */
      notInBill: 'Not in bill',
      variation: 'Variation',
      /**
       * Shown on a row that is on the bill but orderable nowhere. Names the fix and where it
       * lives, because it is not fixable from this screen.
       */
      noSpecTooltip:
        'This item has no specification on any of its BOQ lines, so it can’t be ordered. Add one on the project’s BOQ.',
      noSpecLabel: 'No spec',
      // The accessible name of the disabled control: the visible label, then the item it belongs to.
      noSpecName: 'No spec, {name}',
      // Short on purpose: it is a flag on a dense row, and the money detail belongs to Allocate.
      variationTooltip:
        'This item is not on the sites’ bills. Adding it raises a variation for approval.',
      /**
       * `vendorId` is nullable on the wire. A vendorless item is named rather than left blank,
       * because the vendor is what a bulk order is *for* — a blank group heading in the tray reads
       * as a rendering fault, where this reads as the fact it is.
       */
      unknownVendor: 'No vendor set',
      add: 'Add',
      added: 'Added',
      addLabel: 'Add {name}',
      removeLabel: 'Remove {name} from the selection',
    },

    types: {
      material: 'Material',
      labour: 'Labour',
      plant: 'Plant & equipment',
    },

    tray: {
      trigger: 'Selected',
      triggerLabel: 'Selected items ({count})',
      title: 'Selected items',
      close: 'Close',
      empty: 'No items yet. Add from the bill.',
      /** Numbered in the order the vendors were added, so the numbering is stable. */
      vendorGroupChip: 'Bulk order {index}',
      remove: 'Remove {name}',
      itemMeta: '{rate} /{unit} · {unitType}',
      // Pluralised: the count reaches 1 whenever a bill line sits on a single site.
      itemMetaOnBill:
        '{rate} /{unit} · {unitType} · on {count} site | {rate} /{unit} · {unitType} · on {count} sites',
    },

    empty: {
      /** A filter matched nothing. Deliberately distinct from the no-project and empty-bill states. */
      noMatches: {
        title: 'No items match your filters',
        description:
          'Nothing on the bill matches the type or vendor you picked. Clear the filters to see everything.',
      },
      /** The bill genuinely has no lines. Its own copy, and the catalogue opens on arrival. */
      emptyBill: {
        title: 'No items on this project’s bill',
        description:
          'This project’s bill has no line items yet. Search the full vendor catalogue below to add one. Off-bill items are flagged as a variation.',
      },
      noCatalogueMatches: 'No catalogue items match your filters.',
    },

    pagination: {
      /** The shared paginator gives the page nav but not this count, so it is composed alongside. */
      showing: 'Showing {from}–{to} of {total} {noun}',
      billNoun: 'bill items',
      catalogueNoun: 'catalogue items',
    },

    errors: {
      /** Inline or toast — this step has no whole-page error state. */
      billItems: 'Could not load this project’s bill items. Please try again.',
      catalogueItems: 'Could not load the vendor catalogue. Please try again.',
    },
  },

  /**
   * The draw-period treatment (PE-672, FR-56a).
   *
   * ⚠️ **`standings.dueNow.definition` and `provenance` are shared wording, not this step's own.**
   * Batch A.2b's Sites-step "Due now" filter chip reads the same two strings through
   * `components/BulkOrders/Create/Draw/draw-standing.ts`, so the two steps cannot drift into two
   * vocabularies for one concept. Change them here and both move.
   */
  draw: {
    standings: {
      overdue: {
        label: 'Overdue',
        definition: 'The draw period has passed and there is still quantity to order.',
      },
      dueNow: {
        label: 'Due now',
        definition: 'The draw period is current, or has passed with quantity still to order.',
      },
      upcoming: {
        label: 'Upcoming',
        definition: 'The draw period has not started yet.',
      },
    },

    /** Never counted towards the tag, but shown in the split so the counts add up to the item's sites. */
    nonDriving: {
      noDrawData: 'No draw set',
      fullyOrdered: 'Fully ordered',
      notCounted: 'Not counted towards the tag',
    },

    tooltip: {
      /** One line per state, never one per site. */
      acrossSites: 'Across {count} site on this item | Across {count} sites on this item',
      siteCount: '{count} site | {count} sites',
      collapsed: '{label} at {count} site | {label} at {count} sites',
      /**
       * Pluralised because the singular has to lose the "all": one site read "Overdue at all 1
       * sites". "this item's" rather than "its", so the possessive cannot be read as belonging to
       * the state — and it matches `acrossSites`' phrasing.
       */
      collapsedAll: '{label} at this item’s only site | {label} at all {count} sites',
      tagChosenBy: 'Tagged by the most urgent state across these sites.',
      window: '{start} to {end}',
      windowFrom: 'from {start}',
      windowUntil: 'until {end}',
      draws: 'Draws {labels}',
      draw: 'Draw {label}',
    },

    /** Where the data came from. Shared with the Sites step — see the note above. */
    provenance: 'From the draw period set on each site’s BOQ line item.',

    /**
     * States the active ordering, because the list is flat — no group headings — so the ranking is
     * otherwise invisible. Read-only: not a control, no sort state, no interactive role.
     */
    sortCaption: 'Sorted by:',
    sortCaptionValue: 'due now first',
  },

  /**
   * Step 4 — Review (S-06).
   *
   * ⚠️ **No em dashes and no spaced en dashes anywhere in this block.** PE-691 makes it a criterion
   * for the commit surface, and the two halves of one screen cannot punctuate differently. Where
   * the design source uses one (its fallback line does), it becomes a colon or the clause goes.
   */
  /**
   * Step 4, Payment and delivery (S-11).
   *
   * ⚠️ **No em dashes and no spaced en dashes anywhere in this block**, on the same criterion the
   * review block below carries: the two steps are read one after the other and cannot punctuate
   * differently.
   *
   * Release 1 collects the delivery date and nothing else. The payment blocks are PE-882's.
   */
  payment: {
    delivery: {
      /** Names the row and the control both: a floating label over it would say it twice. */
      label: 'Delivery date',
      placeholder: 'Choose a date',
      /**
       * The plural adds the one fact that is not obvious: several orders, one date. A per-site date
       * is a different feature.
       */
      helpOne: 'When the items will be delivered.',
      helpMany: 'When the items will be delivered. Every order gets this date.',
    },

    backToReview: 'Back to Review',

    /**
     * What the shared vendor-payments table cannot say for itself, because it names the document.
     * Everything else it shows lives in the neutral `vendorPayments.*` namespace.
     */
    table: {
      requestType: { order: 'Order' },
      columns: { deposit: 'Deposit' },
      tooltips: {
        method:
          'Sets how this order is paid. Cash on delivery falls due on the date you set, and an account payment on the vendor’s account terms.',
        deposit:
          'Splits the order into two payment requests: the deposit, and one for the balance. Enter it as a percentage or as an amount.',
      },
    },

    /**
     * The shared documentation drawer's copy, which names the document throughout. Staged here and
     * filed against each order once it is placed.
     */
    documentation: {
      title: 'Order documentation',
      infoMessage:
        'These documents are optional here. They are attached to the order once it is placed.',
      accordionTitle: 'Key documentation',
      accordionSubtitle: 'The documents this order and its payments rely on.',
      prerequisitesTitle: 'Order prerequisites',
      quoteLabel: 'Quote/Proforma Invoice',
      invoiceLabel: 'Tax Invoice',
      fileHint: 'PDF, JPG or PNG · up to 10MB per file · Multiple files supported',
      done: 'Done',
    },

    block: {
      standardOrder: 'Standard order',
      /** Plain rather than apologetic: it is a different order, not a lesser one (CR-25). */
      standardNote: 'Fewer than 2 sites, so this is placed as a standard order.',
      toSet: '1 field to set | {count} fields to set',
      /** Review's payment line names an account by its term. */
      account: '{term} account',
    },

    method: {
      cod: 'Cash on delivery',
      account: 'Account',
      required: 'Choose how this order is paid.',
    },

    deposit: {
      required: 'Enter the deposit, or remove it.',
      percentError: 'Enter a percentage from 0.01 to 99.99.',
    },

    gates: {
      /** One field left names it, as the design does; more than that counts the orders. */
      oneField: '1 field still to set: {vendor} {field}',
      fields: {
        method: 'payment method',
        deposit: 'deposit',
      },
      needsAttention:
        '1 order needs payment details to continue. | {count} orders need payment details to continue.',
    },

    errors: {
      preview: 'The order totals could not be loaded, so nothing can be placed yet.',
      retry: 'Try again',
    },
  },

  review: {
    /** Beside the disabled footer action while a partial placement's recovery banner is showing. */
    recoveryFooter: 'Retry or abandon the placement above to continue.',

    /** After a clean placement: the orders exist, so this is never reported as a failed one. */
    documentsNotFiled: {
      title:
        'Your orders were placed, but 1 document could not be attached | Your orders were placed, but {count} documents could not be attached',
      content: 'Upload them from the order once it opens.',
    },

    headline: {
      /** Assembled from the parts below, so "2 bulk orders + 1 standard order" needs no variant. */
      willBeCreated: '{orders} will be created',
      bulkOrders: '1 bulk order | {count} bulk orders',
      standardOrders: '1 standard order | {count} standard orders',
      sublineMultiVendor:
        'One tile per resulting order. Select a tile to review what that order contains.',
      sublineSingleVendor: 'Review the order contents below before placing.',
      sublineSingleSite: 'Covers {site}.',
    },

    summary: {
      orderTotal: 'Order total',
      sites: 'Sites',
      items: 'Items',
      /**
       * 🔴 "Needing" was wrong for half of what this counts. The tile reads
       * `variationSiteCount`, which is every site raising a variation — and a decrease is exempt
       * from individual approval (FR-13, DL-52), so a site raising only a saving needs nothing from
       * anyone. Neutral, and true either way (CPTO, 2026-09-22).
       */
      variations: 'Sites raising a variation',
      dates: 'Sites outside their date range',
      /** Shortened to the noun below `lg`, where the icon is already carrying which flag it is. */
      short: {
        sites: 'Sites',
        items: 'Items',
        variations: 'Variations',
        dates: 'Outside dates',
      },
    },

    perSite: {
      heading: 'Ordered per site',
      caption:
        "1 site, its total against its remaining budget on the lines this order touches | {count} sites, each site's total against its remaining budget on the lines this order touches",
      show: 'Show',
      hide: 'Hide',
      share: '{share}% of {budget} remaining budget',
      overBy: 'Over by {amount}',
      /** The bar announces only a percentage, so its name has to carry the subject. */
      barLabel:
        "{site} spend, {share}% of the site's remaining budget on the lines this order touches",
      /**
       * 🔴 `isOverBudget` is the site's NET position across the lines this order touches, so
       * these may not claim "on at least one line" — a site over on one line and under on another
       * nets to within budget and still raises a variation. The variation count says that; this
       * says only what it can (CPTO, 2026-09-21).
       */
      someOverBudget:
        '{over} of {total} sites will go over its BOQ budget | {over} of {total} sites will go over their BOQ budget',

      /**
       * The bar is a proportion, and a proportion cannot state the figures behind it. Labels are
       * nouns rather than sentences: the tooltip is a key-and-value read, not prose.
       */
      tooltip: {
        spend: 'This order',
        budget: 'Remaining budget',
        overLabel: 'Over budget by',
        within: 'Uses {share}% of the budget left on these lines',
        /**
         * Stated separately from the over-budget line, and shown even where the site nets out
         * within budget, because the two are different facts: over-budget is the site's net
         * position, a variation is raised on the individual line that went over.
         *
         * ⚠️ **What approval costs is deliberately NOT repeated here** (CPTO, 2026-09-21). The tag
         * hover says it on the row it applies to, and the confirmation modal says it at the moment
         * of commitment. A third copy on a hover a user may never open buys nothing.
         */
        /**
         * ⚠️ **Direction comes from the GATING count, and it has to.** A variation gates exactly
         * when it increases the line total (FR-20, DL-52), so gating is the server's own answer to
         * "does this put the budget up". Stating "increasing" for every variation told the officer
         * a rate reduction was raising their budget (CPTO, 2026-09-22).
         */
        raisesUp:
          'Raises 1 variation, increasing this site’s BOQ budget | Raises {count} variations, increasing this site’s BOQ budget',
        raisesDown:
          'Raises 1 variation, lowering this site’s BOQ budget | Raises {count} variations, lowering this site’s BOQ budget',
        raisesMixed: 'Raises {count} variations, {up} of which increase this site’s BOQ budget',
      },
    },

    tiles: {
      heading: 'Resulting orders',
      footnote: '1 site | {count} sites',
      footnoteStandard: '1 site, standard order | {count} sites, standard order',
      /** Marks a fallback tile without reading as a lesser thing (DL-47). */
      standardMarker: 'Standard',
      variationCount: '1 site needs a variation | {count} sites need a variation',
      dateCount: '1 site outside its date range | {count} sites outside their date range',
    },

    card: {
      kindBulk: 'Bulk order',
      kindStandard: 'Standard order',
      orderTotal: 'Order total',
      siteCount: '1 site | {count} sites',
      /**
       * 🔴 States what will be created and does not apologise (DL-47, and the 2026-08-04 review).
       * No warning tone, no "unfortunately", and no pointer at a different flow: this is the first
       * point the user is told, deliberately.
       */
      fallbackNote:
        'Covers 1 site, so this is created as a standard order: a bulk order needs at least 2 sites.',
    },

    flags: {
      /**
       * 🔴 **Two tags, because they are two events.** An increase needs someone to approve it and
       * blocks the bulk order until they do; a decrease is raised, visible and pending and clears
       * on the cascade (FR-20, DL-52). Labelling a saving "Variation needed" asks the officer to
       * act on something that needs nothing, and colouring it orange says it is a problem (CPTO,
       * 2026-09-22).
       */
      variationNeeded: 'Variation needed',
      variationSaving: 'Variation raised',
      outsideDateRange: 'Outside date range',
      /**
       * What each flag means for the user, on the tag itself (CPTO, 2026-09-21). It replaces a
       * standing note under the table: the meaning belongs on the thing it describes, and the full
       * account of what Request does already lives in the confirmation modal.
       *
       * 🔴 **The two variation cases are not the same event.** An increase gates the whole bulk
       * order until it is cleared on its own site; a decrease is raised, visible and pending, and
       * is cleared by the cascade with everything else (FR-20, DL-52).
       */
      hover: {
        variationGating:
          'Ordering this increases the BOQ budget at this site, so a variation is raised there.',
        variationGatingEffect:
          'The bulk order cannot be approved until it is approved at that site.',
        variationSaving:
          'Ordering this lowers the BOQ budget at this site, so a variation is raised there.',
        variationSavingEffect:
          'Nothing to approve separately now. It is approved with the bulk order, where the approver chooses whether the new rate is permanent or for this order only.',
        /** Both figures are the preview's own (PE-955); neither is composed here. */
        thisOrderMovement: 'Moves the BOQ line total by {amount} on this order.',
        permanentMovement: 'If the approver makes the rate permanent: {amount}',
        /** A standard order is approved permanent with no choice (DL-150), so one figure. */
        standardMovement: 'Approving this variation moves the BOQ line total by {amount}.',
        movementInputs: '{quantity} at {orderRate}, line rate {lineRate}',
        /**
         * ⚠️ **At approval, not at placement.** `EnsureSiteDateRange` is step 1 of the standard
         * order's APPROVE workflow (`ApproveOrderContext`), dispatched with today's date, and bulk
         * reuses it unchanged — so the window moves when the order is approved, to cover that day.
         * The copy this replaced said "when the order is created", which followed FR-58; DL-133
         * G-03 corrects it against the code, and standard-order behaviour is what we follow.
         */
        /**
         * The item row speaks about several sites at once, so it gets its own phrasing rather than
         * the site row's singular. The effect lines below are shared: what it means for the user is
         * the same fact at either altitude.
         */
        variationGatingItem: 'These sites raise a variation that increases their BOQ budget.',
        variationSavingItem: 'These sites raise a variation that lowers their BOQ budget.',
        dateWindowItem: 'These sites fall outside their current dates.',
        dateWindow: 'This order falls outside the site’s current dates.',
        dateWindowEffect:
          'Approving it moves the site’s dates to cover it. It does not block this request.',
      },
    },

    money: {
      exclVat: '{amount} excl VAT',
      /** The same figure as a label rather than a suffix, for a key-and-value layout. */
      exclVatLabel: 'Excl VAT',
    },

    /** The selected order's contents, grouped by item with its sites nested (DL-53). */
    breakdown: {
      columns: {
        item: 'Item',
        sites: 'Sites',
        quantity: 'Quantity',
        amount: 'Amount',
      },
      /** Names the rows, not the gesture: a bare chevron gets no other name. */
      expand: 'Show the sites for {item}',
      collapse: 'Hide the sites for {item}',
      /** The mark's title on pointer, and its tapped line on the mobile ladder. */
      flagMark: {
        variation: 'Variation needed at {sites}',
        date: 'Outside date range at {sites}',
      },
      flagMarkAction: 'Which sites?',
      cardMeta: '{sites} sites · {quantity} {unit}',
      /**
       * Zero rows here is always a failure, never a legitimately empty order: this order exists
       * because it has items. So the copy says the breakdown could not be shown rather than that
       * there is nothing in it, and it says which figures are still trustworthy.
       */
      unavailableTitle: 'The item breakdown could not be shown',
      unavailableBody:
        'The order totals above are unaffected, and this order will be created exactly as summarised.',
    },

    /**
     * DL-132. Named as a count of rows rather than of figures: a row is what the officer can act
     * on, and several figures on one row moving is still one decision.
     */
    stale: {
      returned:
        'One row changed while you were allocating. Its figures are updated below. | {count} rows changed while you were allocating. Their figures are updated below.',
    },

    /**
     * How this order will be paid, restated from what step 4 collected (PE-883).
     *
     * ⚠️ Release 1 builds the line and nothing produces one yet — the payment state is PE-882's.
     * The separator is a middle dot rather than a dash, on this block's no-em-dash criterion.
     */
    payment: {
      methodWithDeposit: '{method} · {deposit}',
      depositClause: '{percentage}% deposit, {amount}',
    },

    /** The commit action and its count line (PE-691). */
    action: {
      requestAll: 'Request all orders',
      requestOne: 'Request order',
      creating: 'Creating…',
      creates: 'Creates {orders}',
      /** The DL-47 fallback names its site and states it plainly, with no downgrade framing. */
      createsStandardAt: 'Creates 1 standard order at {site}',
    },

    /** DL-37's confirm-the-changes modal. */
    confirm: {
      title: 'Confirm the changes to these sites',
      subtitleOne: 'Requesting this order changes the sites below.',
      subtitleMany: 'Requesting these {count} orders changes the sites below.',
      confirm: 'Confirm and request',
      submitting: 'Requesting…',
      cancel: 'Cancel',
      range: '{start} to {end}',

      dates: {
        heading: 'Date range changes',
        /**
         * ⚠️ **At approval, not at placement.** `EnsureSiteDateRange` is step one of the standard
         * order's APPROVE workflow, dispatched with the date of that approval, and bulk reuses it
         * unchanged (DL-133 G-03). So the new range below is what approving today would give; a
         * bulk order approved three weeks from now extends further than this predicts, which is
         * why it says "at least".
         */
        lead: 'Approving this order extends these sites’ dates to cover the approval date.',
        approxNote: 'The new range shows what approving today would give.',
        site: 'Site',
        current: 'Current range',
        next: 'New range',
        /** Load-bearing: FR-58 / DL-51 writes the change to each site's child order. */
        recorded: 'This change is recorded on each site’s order.',
        /** The one reason a placement that moves any site's dates asks for (DL-78). */
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Why are these orders outside the site dates?',
        reasonHint: 'This reason is saved to each moved site’s activity log.',
      },

      variations: {
        heading: 'Variations raised',
        lead: 'This order raises a variation on these sites.',
        /**
         * The reason the modal exists (DL-37): approving the bulk order approves no variation.
         *
         * 🔴 **Said only where one actually gates.** A decrease needs no sign-off at all — it is
         * raised, visible and pending, and clears on the cascade (FR-20, DL-52) — so stating it
         * unconditionally told an officer a rate reduction was holding their order up (CPTO,
         * 2026-09-22). The same mistake the tags made, at the moment of commitment.
         */
        approvedSeparately:
          'One of these must be approved on its own site before the bulk order can be approved. | Each of these must be approved on its own site before the bulk order can be approved.',
        approvedSeparatelySome:
          '{count} of these must be approved on its own site before the bulk order can be approved.',
        /** The reductions' half of the note (DL-149): no sign-off now, and a scope chosen later. */
        noneGate:
          'None of them holds up approval: they are approved with the bulk order, at a scope the approver chooses then.',
        othersWithBulk:
          'The others are approved with the bulk order, at a scope the approver chooses then.',
        site: 'Site',
        line: 'BOQ line',
        reason: 'Reason',
        change: 'Value change',
        /** The inputs the server's figure arises from, stated rather than combined. */
        changeInputs: '{quantity} at {orderRate}, line rate {lineRate}',
      },

      /** `VariationCause`, rendered verbatim rather than mapped to a local sentence. */
      causes: {
        None: 'No change',
        NewOrOffBillLine: 'New line',
        OverBudgetQuantity: 'Over budget',
        RateChange: 'Rate change',
      },
    },

    /**
     * The placement loading treatment: two lines, naming the operation and setting an honest
     * expectation. Fresh copy under its own key, because the string FE-SDD §9 quotes does not exist
     * in this codebase and the key it would have reused belongs to the BOQ export.
     */
    placing: {
      title: 'Creating your orders',
      body: 'Creating your orders and reserving each site’s budget.\nThis usually takes a few seconds, and longer for a large order.',
    },

    errors: {
      /**
       * ⚠️ Accurate only where nothing was written, which is why a partial placement never reaches
       * it: that one routes to its own outcome surface instead.
       */
      placement: 'The orders could not be requested. Nothing was created and no site was changed.',
      /** Review re-pricing the grid on entry (PE-981). Nothing is placeable until it succeeds. */
      preview: 'The order totals could not be loaded, so nothing can be placed yet.',
      retry: 'Try again',
      /**
       * A conflict this step cannot read. Something may have been written, so this neither promises
       * nothing was created nor invites a straight retry, which could place the order twice.
       */
      unconfirmed:
        'We could not confirm whether your orders were placed. Check Bulk Orders before requesting them again.',
      /** The orders exist; only the move to the list failed. Never the "nothing was created" line. */
      placedNotLanded:
        'Your orders were placed, but this page could not move on. Open Bulk Orders to find them.',
      /** The abandon completed; only the move to the list failed. */
      abandonedNotLanded:
        'The bulk order was abandoned, but this page could not move on. Open Bulk Orders to continue.',
    },
  },
};
